<template>
  <div>
    <SwitchingBehaviour
      ref="switchingBehaviour"
      :requires-analytics-text="tkey('requiresAnalyticsRun')"
      tooltip-link="toolguide/080-switching.html"
      :job-type="switchingJob"
      :service-job-cann-groups="serviceJobCannGroups"
      :service-job-progress="serviceJobProgress"
      @run-switching="runSwitching"
    />
    <DependencyTreeFeedbackModal
      :value="dependencyTreeModalOpen"
      :results="dependencyTreeFeedback"
      page="switching"
      @close="closeDependencyTreeModal"
      @commit="runSwitching(true)"
    />
  </div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { useInternationalization } from '@/composables/use-internationalization';
import { useFeature } from '@/composables/use-feature';
import { useScenarioStatusFormatter } from '@/composables/use-scenario-status-formatter';
import { useAiAgentActions } from '@/composables/use-ai-agent-actions';
import { useUnclusteredSwitchingStatus } from '@/composables/use-unclustered-switching-status';
import { useScenariosStore } from '@/stores/scenarios';
import { useSwitchingMatricesStore } from '@/stores/switching-matrices';
import { useAiStore } from '@/stores/ai';
import JobAPI from '@enums/jobapi';
import cannGroupUtils from '@/utils/cann-groups';
import { mapActions, mapState } from 'pinia';

export default {
  setup() {
    const { tkey } = useInternationalization('switchingWrapper');
    const { aiPanelEnabled, productSwitchingServiceEnabled } = useFeature();
    const { formatScenarioProgress } = useScenarioStatusFormatter('switching');
    const instance = getCurrentInstance();
    const scenariosStore = useScenariosStore();

    const {
      serviceJobCannGroups,
      serviceJobProgress,
      start: startPoller,
    } = useUnclusteredSwitchingStatus({
      getScenarioId: () => instance.proxy.$route.params.scenarioId,
      onTerminal: async () => {
        await scenariosStore.loadScenario(scenariosStore.selectedScenario._id);
      },
    });

    useAiAgentActions({
      allowScenarioWorkflowNavigation: true,

      getExclude: () => {
        const child = instance.proxy.$refs.switchingBehaviour;
        return child?.isEditingDisabled ? ['run-switching'] : [];
      },

      getSupplementary: () => {
        const child = instance.proxy.$refs.switchingBehaviour;
        const scenarioCannGroups = cannGroupUtils.getLeafNodes(
          instance.proxy.selectedScenario?.cannGroups || []
        );
        const cannGroupList = scenarioCannGroups.length
          ? scenarioCannGroups.map(cg => `- ${cg.name} (key: ${cg.key})`).join('\n')
          : 'None available';
        const attributeList = child?.scenarioAttributesList?.length
          ? child.scenarioAttributesList.join(', ')
          : 'None available';
        return {
          'select-cann-group': `Available cann groups:\n${cannGroupList}`,
          'set-attribute': `Available attributes: ${attributeList}`,
        };
      },

      actions: {
        'run-switching': {
          description:
            'Run the switching analysis job for the current scenario. Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Running switching analysis',
          confirmationLabel: 'run switching analysis',
          parameters: {
            type: 'object',
            properties: {},
            required: [],
          },
          handler: async () => {
            const child = instance.proxy.$refs.switchingBehaviour;
            if (child?.isAnyJobRunning) {
              return '[Tool] Action failed: a switching job is already running. Ask the user to wait for it to complete.';
            }
            await instance.proxy.runSwitching();
            if (instance.proxy.dependencyTreeModalOpen) {
              return '[Tool] Action succeeded: switching job triggered, but dependency tree feedback is required. Inform the user that a feedback modal has appeared and they need to review and commit it before proceeding.';
            }
            return '[Tool] Action succeeded: switching analysis job has been triggered and is now running in the background.';
          },
        },

        'select-cann-group': {
          description:
            'Select a cann group to view its switching matrix. Call this before answering questions about a specific cann group so the user can see the relevant matrix. Executes silently without a confirmation prompt.',
          readOnly: true,
          workingLabel: 'Selecting cann group',
          parameters: {
            type: 'object',
            properties: {
              cannGroupName: {
                type: 'string',
                description:
                  'The exact name of the cann group to select, as shown in the available cann groups list.',
              },
            },
            required: ['cannGroupName'],
          },
          handler: async ({ cannGroupName }) => {
            const child = instance.proxy.$refs.switchingBehaviour;
            const allGroups = cannGroupUtils.getLeafNodes(
              instance.proxy.selectedScenario?.cannGroups || []
            );
            const match = allGroups.find(
              cg => cg.name?.toLowerCase() === cannGroupName?.toLowerCase()
            );
            if (!match) {
              return `[Tool] Action failed: cann group "${cannGroupName}" not found. Ask the user for the exact cann group name from the available list.`;
            }
            child?.selectCannGroup(match);
            return `[Tool] Action succeeded: selected cann group "${match.name}".`;
          },
        },

        'set-view-mode': {
          description:
            'Switch the switching matrix view between attribute-level and product-level. Executes silently without a confirmation prompt.',
          readOnly: true,
          workingLabel: 'Switching view mode',
          parameters: {
            type: 'object',
            properties: {
              mode: {
                type: 'string',
                enum: ['attribute', 'product'],
                description:
                  'The view mode to switch to: "attribute" for attribute-level switching, "product" for product-level switching.',
              },
            },
            required: ['mode'],
          },
          handler: async ({ mode }) => {
            const child = instance.proxy.$refs.switchingBehaviour;
            if (!child) {
              return '[Tool] Action failed: switching component is not available.';
            }
            child.viewBySelection = mode;
            return `[Tool] Action succeeded: switched to ${mode}-level view.`;
          },
        },

        'set-attribute': {
          description:
            'Select a specific attribute to view in attribute-level switching mode. Automatically switches to attribute view if currently in product view. Executes silently without a confirmation prompt.',
          readOnly: true,
          workingLabel: 'Selecting attribute',
          parameters: {
            type: 'object',
            properties: {
              attributeName: {
                type: 'string',
                description:
                  'The exact name of the attribute to select, as shown in the available attributes list.',
              },
            },
            required: ['attributeName'],
          },
          handler: async ({ attributeName }) => {
            const child = instance.proxy.$refs.switchingBehaviour;
            if (!child) {
              return '[Tool] Action failed: switching component is not available.';
            }
            const available = child.scenarioAttributesList || [];
            const match = available.find(a => a.toLowerCase() === attributeName?.toLowerCase());
            if (!match) {
              return `[Tool] Action failed: attribute "${attributeName}" not found. Ask the user for the exact attribute name from the available list.`;
            }
            // Ensure we are in attribute view before setting the attribute
            child.viewBySelection = 'attribute';
            child.selectedAttribute = match;
            return `[Tool] Action succeeded: switched to attribute view and selected attribute "${match}".`;
          },
        },
      },
    });

    return {
      tkey,
      aiPanelEnabled,
      productSwitchingServiceEnabled,
      serviceJobCannGroups,
      serviceJobProgress,
      startPoller,
      formatScenarioProgress,
    };
  },

  data() {
    return {
      switchingJob: JobAPI.jobTypes.switching,
      dependencyTreeFeedback: {},
      dependencyTreeModalOpen: false,
    };
  },

  computed: {
    ...mapState(useScenariosStore, ['selectedScenario', 'jobStatuses']),
  },

  mounted() {
    const aiStore = useAiStore();
    const scenarioName = this.selectedScenario?.name || 'Switching';
    aiStore.enableSidePanel({ section: 'switching', contextDisplay: [scenarioName, 'Switching'] });
    aiStore.setSidePanelContextFactory(() => this.buildInnerContext());
    // Safe to set immediately: ScenarioWorkflow's v-if gate ensures selectedScenario
    // is fully populated before this page mounts.
    aiStore.setContextReady(true);
  },

  methods: {
    ...mapActions(useSwitchingMatricesStore, ['runSwitchingJob']),
    ...mapActions(useScenariosStore, ['loadScenario']),

    async runSwitching(commit = false) {
      const result = await this.runSwitchingJob({
        params: { scenarioId: this.$route.params.scenarioId },
        commit,
      });
      if (result.data.needsFeedback) {
        this.dependencyTreeFeedback = result.data.output;
        this.dependencyTreeModalOpen = true;
      } else {
        await this.refreshSelectedScenario();
        this.startPoller();
      }
    },

    async refreshSelectedScenario() {
      await this.loadScenario(this.selectedScenario._id);
    },

    closeDependencyTreeModal() {
      this.dependencyTreeModalOpen = false;
      this.dependencyTreeFeedback = {};
    },

    /**
     * Build the inner context object for the AI assistant (called lazily at send-time).
     */
    buildInnerContext() {
      const switchingBehaviour = this.$refs.switchingBehaviour;
      const selectedCannGroup = switchingBehaviour?.selectedCannGroup;
      const viewBySelection = switchingBehaviour?.viewBySelection || 'attribute';
      const selectedAttribute = switchingBehaviour?.selectedAttribute;
      const isJobComplete = switchingBehaviour?.isJobComplete || false;
      const isAnyJobRunning = switchingBehaviour?.isAnyJobRunning || false;

      // Determine switching status
      let switchingStatus = 'Not started';
      if (this.jobStatuses?.switching?.status === 'finished') {
        switchingStatus = 'Complete ✓';
      } else if (this.jobStatuses?.switching?.status === 'running') {
        const progress = this.productSwitchingServiceEnabled
          ? this.serviceJobProgress
          : this.jobStatuses.switching.progress;
        switchingStatus = `In Progress → (${progress}%)`;
      } else if (this.jobStatuses?.switching?.status === 'failed') {
        switchingStatus = 'Failed ✗';
      }

      // Build available cann groups list
      const leafCannGroups = cannGroupUtils.getLeafNodes(this.selectedScenario?.cannGroups || []);
      const availableCannGroups = leafCannGroups.length
        ? leafCannGroups.map(cg => `- ${cg.name} (key: ${cg.key})`).join('\n')
        : 'None available';

      // Build available attributes list
      const attributesList = switchingBehaviour?.scenarioAttributesList || [];
      const availableAttributes = attributesList.length
        ? attributesList.join(', ')
        : 'None available';

      return {
        scenarioId: this.selectedScenario?._id,
        scenarioName: this.selectedScenario?.name || 'No scenario selected',
        cannGroupName: selectedCannGroup?.name || 'No Cann Group selected',
        currentView: viewBySelection,
        scenarioProgress: this.formatScenarioProgress,
        switchingStatus,
        selectedAttribute:
          viewBySelection === 'attribute'
            ? selectedAttribute || 'None selected'
            : 'N/A (Product view)',
        analysisComplete: isJobComplete,
        isJobRunning: isAnyJobRunning,
        availableCannGroups,
        availableAttributes,
      };
    },
  },
};
</script>
